import Highcharts from 'highcharts';
import {
  teal,
  blue,
  green,
  purple,
  orange,
  cyan,
  red,
  gray,
} from '@carbon/colors';

// Palette aligned with the KPI accent colors, then extended for additional series.
export const CHART_PALETTE = [
  teal[60], // #007d79
  blue[60], // #0f62fe
  green[50], // #24a148
  purple[60], // #8a3ffc
  orange[40], // #ff832b
  cyan[50], // #1192e8
  red[50], // #fa4d56
  purple[50], // #a56eff
];

const FONT = "'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif";

// Shared height for every chart in this feature so they line up on the grid.
export const CHART_HEIGHT = 340;

// Deep-merges a Carbon-flavored theme under each chart's own options. Applied
// locally (not via Highcharts.setOptions) so it doesn't leak to other pages.
export const themed = (overrides: Highcharts.Options): Highcharts.Options =>
  Highcharts.merge(
    {
      credits: { enabled: false },
      colors: CHART_PALETTE,
      chart: {
        backgroundColor: 'transparent',
        style: { fontFamily: FONT },
        spacing: [12, 8, 8, 8],
      },
      title: { text: undefined },
      xAxis: {
        lineColor: gray[20],
        tickColor: gray[20],
        labels: { style: { color: gray[70], fontSize: '11px' } },
        title: { style: { color: gray[60], fontSize: '11px' } },
      },
      yAxis: {
        gridLineColor: '#e8e8e8', // no exact @carbon/colors scale value (between gray[10]/[20])
        gridLineDashStyle: 'Dash',
        labels: {
          style: { color: gray[70], fontSize: '11px' },
          // Default axis labels drop thousands separators below 10k; force comma
          // grouping so ticks read 1,000 / 2,000 not 1000. Millions still abbreviate
          // to M so token axes stay readable (2M, not 2,000,000).
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            const value = this.value as number;
            if (Math.abs(value) >= 1_000_000) {
              return `${Highcharts.numberFormat(
                value / 1_000_000,
                -1,
                '.',
                ','
              )}M`;
            }
            return Highcharts.numberFormat(value, -1, '.', ',');
          },
        },
        title: { style: { color: gray[60], fontSize: '11px' } },
      },
      legend: {
        itemStyle: { color: gray[80], fontWeight: '400', fontSize: '12px' },
        itemHoverStyle: { color: gray[100] },
      },
      tooltip: {
        backgroundColor: gray[90],
        borderWidth: 0,
        borderRadius: 8,
        shadow: false,
        style: { color: gray[10], fontSize: '12px' },
      },
      plotOptions: {
        series: { animation: { duration: 700 } },
        area: { marker: { enabled: false } },
        column: { borderRadius: 2, borderWidth: 0 },
      },
    },
    overrides
  );
